const LITRES_PER_US_GALLON = 3.78541;
// ─────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────
function fail(reason) {
    return { ok: false, reason };
}
function dose(mL) {
    return { ok: true, mL };
}
/** Return the absolute dose, rounded to 2 decimal places. */
function formatML(value) {
    return Math.round(Math.abs(value) * 100) / 100;
}
function parsePositiveFloat(raw) {
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0)
        return null;
    return n;
}
function parseNumber(raw) {
    if (raw.trim() === '')
        return null;
    const n = Number(raw);
    if (!Number.isFinite(n))
        return null;
    return n;
}
// ─────────────────────────────────────────────────────────────────────
// Public
// ─────────────────────────────────────────────────────────────────────
/**
 * Compute a one-time correction dose for a parameter-based solution.
 *
 * Formula:
 *   dose (mL) = (target - current) × tankGallons × L_PER_GAL
 *                / (raise × ref_vol_L)
 */
export function computeCorrectionDose(params) {
    const { solution, tankGallons, currentLevel, targetLevel } = params;
    if (tankGallons <= 0)
        return fail("System volume must be greater than zero.");
    if (solution.ref_vol_L === 0)
        return fail("Calculation not available for this supplement.");
    const levelChange = targetLevel - currentLevel;
    if (levelChange === 0)
        return fail("Current and target levels are the same. No dose needed.");
    const raw = (levelChange * tankGallons * LITRES_PER_US_GALLON) / (solution.raise * solution.ref_vol_L);
    if (raw < 0) {
        if (solution.raise > 0)
            return fail("Target level is lower than current level — this supplement raises parameters, it cannot lower them.");
        return fail("Target level is higher than current level — this supplement removes parameters, it cannot raise them.");
    }
    return dose(formatML(raw));
}
/**
 * Convenience wrapper that takes string inputs (from form fields) and
 * returns the same `DoseResult` discriminator — no thrown strings.
 */
export function computeCorrectionDoseFromStrings(params) {
    const tankGallons = parsePositiveFloat(params.tankGallonsRaw);
    if (tankGallons === null)
        return fail("Please enter a valid system volume.");
    const currentLevel = parseNumber(params.currentLevelRaw);
    if (currentLevel === null)
        return fail("Please enter a valid current level.");
    const targetLevel = parseNumber(params.targetLevelRaw);
    if (targetLevel === null)
        return fail("Please enter a valid target level.");
    return computeCorrectionDose({
        solution: params.solution,
        tankGallons,
        currentLevel,
        targetLevel,
    });
}
/**
 * Compute a daily maintenance dose to replace measured daily consumption.
 *
 * Formula:
 *   dose (mL) = consumption × tankGallons × L_PER_GAL
 *                / (raise × ref_vol_L)
 */
export function computeDailyDose(params) {
    const { solution, tankGallons, dailyConsumption } = params;
    if (tankGallons <= 0)
        return fail("System volume must be greater than zero.");
    if (dailyConsumption === 0)
        return fail("Daily consumption must be non-zero.");
    const raw = (dailyConsumption * tankGallons * LITRES_PER_US_GALLON) / (solution.raise * solution.ref_vol_L);
    if (!Number.isFinite(raw))
        return fail("Invalid calculation. Please check your inputs.");
    return dose(formatML(raw));
}
export function computeDailyDoseFromStrings(params) {
    const tankGallons = parsePositiveFloat(params.tankGallonsRaw);
    if (tankGallons === null)
        return fail("Please enter a valid system volume.");
    const dailyConsumption = parseNumber(params.dailyConsumptionRaw);
    if (dailyConsumption === null || dailyConsumption === 0)
        return fail("Please enter a valid daily consumption value.");
    return computeDailyDose({
        solution: params.solution,
        tankGallons,
        dailyConsumption,
    });
}
/**
 * Compute a ratio-based dose (e.g. Coral Snow: 5 mL per 50 gallons).
 */
export function computeRatioDose(params) {
    const { solution, tankGallons } = params;
    if (tankGallons <= 0)
        return fail("System volume must be greater than zero.");
    const raw = (tankGallons / solution.dose_ratio_gallons) * solution.dose_ratio_ml;
    if (!Number.isFinite(raw))
        return fail("Invalid calculation. Please check your inputs.");
    return dose(formatML(raw));
}
export function computeRatioDoseFromStrings(params) {
    const tankGallons = parsePositiveFloat(params.tankGallonsRaw);
    if (tankGallons === null)
        return fail("Please enter a valid system volume.");
    return computeRatioDose({
        solution: params.solution,
        tankGallons,
    });
}
/**
 * Dispatch to the right calculator based on the solution kind.
 */
export function computeDose(params) {
    const s = params.solution;
    if (s.kind === "ratio") {
        return computeRatioDoseFromStrings({
            solution: s,
            tankGallonsRaw: params.tankGallonsRaw,
        });
    }
    if (s.kind === "parameter") {
        // If current+target are supplied, treat as correction; otherwise daily.
        if (params.currentLevelRaw !== undefined && params.targetLevelRaw !== undefined) {
            return computeCorrectionDoseFromStrings({
                solution: s,
                tankGallonsRaw: params.tankGallonsRaw,
                currentLevelRaw: params.currentLevelRaw,
                targetLevelRaw: params.targetLevelRaw,
            });
        }
        if (params.dailyConsumptionRaw !== undefined) {
            return computeDailyDoseFromStrings({
                solution: s,
                tankGallonsRaw: params.tankGallonsRaw,
                dailyConsumptionRaw: params.dailyConsumptionRaw,
            });
        }
        return fail("No calculation mode selected.");
    }
    return fail("This supplement does not have dosing calculations available.");
}
// ─────────────────────────────────────────────────────────────────────
// Nitrate forecast — ammonia-based nitrogen sources
// ─────────────────────────────────────────────────────────────────────
/**
 * Mass ratio of nitrate to ammonia for full nitrification (NH3 → NO3-).
 * Nitrogen is conserved through the process; the ratio is the quotient of
 * the molar masses: M(NO3) / M(NH3) = 62.004 / 17.031 ≈ 3.6406.
 */
export const NITRATE_PER_AMMONIA_MASS_RATIO = 62.004 / 17.031;
/**
 * Forecast the nitrate produced when a planned ammonia dose fully nitrifies.
 *
 * Only valid for solutions flagged `nitrifiesToNitrate`. The ammonia raise is
 * computed from the same `raise`/`ref_vol_L` model used by the dose math, then
 * converted to nitrate via {@link NITRATE_PER_AMMONIA_MASS_RATIO} and added to
 * the operator's current nitrate reading.
 */
export function computeNitrateForecast(params) {
    const { solution, tankGallons, doseML, currentNitrate } = params;
    if (solution.nitrifiesToNitrate !== true) {
        return fail('Nitrate forecast is only available for ammonia-based nitrogen sources.');
    }
    if (tankGallons <= 0)
        return fail('System volume must be greater than zero.');
    if (doseML <= 0)
        return fail('Planned dose must be greater than zero.');
    if (!Number.isFinite(currentNitrate) || currentNitrate < 0) {
        return fail('Please enter a valid current nitrate level.');
    }
    const tankLitres = tankGallons * LITRES_PER_US_GALLON;
    const ammoniaRaise = (doseML * solution.raise * solution.ref_vol_L) / tankLitres;
    const nitrateRaise = ammoniaRaise * NITRATE_PER_AMMONIA_MASS_RATIO;
    const projectedNitrate = currentNitrate + nitrateRaise;
    return { ok: true, ammoniaRaise, nitrateRaise, projectedNitrate };
}
/**
 * Convenience wrapper that takes string inputs (from form fields) and returns
 * the same `NitrateForecastResult` discriminator — no thrown strings.
 */
export function computeNitrateForecastFromStrings(params) {
    const tankGallons = parsePositiveFloat(params.tankGallonsRaw);
    if (tankGallons === null)
        return fail('Please enter a valid system volume.');
    const doseML = parsePositiveFloat(params.doseMLRaw);
    if (doseML === null)
        return fail('Please enter a valid planned dose in mL.');
    const currentNitrate = parseNumber(params.currentNitrateRaw);
    if (currentNitrate === null || currentNitrate < 0) {
        return fail('Please enter a valid current nitrate level.');
    }
    return computeNitrateForecast({
        solution: params.solution,
        tankGallons,
        doseML,
        currentNitrate,
    });
}
//# sourceMappingURL=dose-math.js.map